import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ProductCard from "@/components/ProductCard";
import API, { getImageUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { ArrowRight, Truck, Shield, Heart, RefreshCw } from "lucide-react";

const HERO_IMG = getImageUrl("naura-numa/assets/frontend/home_hero.png");
const CATEGORY_IMG = getImageUrl("naura-numa/assets/frontend/home_category.png");
const FABRIC_BG = getImageUrl("naura-numa/assets/frontend/home_fabric.png");

const categories = [
  { name: "Gamis", icon: "👗" },
  { name: "Couple", icon: "💑" },
  { name: "Family Set", icon: "👨‍👩‍👧‍👦" },
  { name: "Hijab", icon: "🧕" },
  { name: "Kids", icon: "👶" },
  { name: "Men", icon: "👔" },
];

const getPoStatusRank = (product) => Number(product?.is_po_open_calculated ?? product?.is_po_open ?? 1) === 1 ? 0 : 1;

const getPoCloseTime = (product) => {
  if (!product?.preorder_closing_date) return Number.NEGATIVE_INFINITY;
  const time = new Date(product.preorder_closing_date).getTime();
  return Number.isFinite(time) ? time : Number.NEGATIVE_INFINITY;
};

const sortProductsByClosePo = (items = []) => {
  return [...items].sort((a, b) => {
    const aStatusRank = getPoStatusRank(a);
    const bStatusRank = getPoStatusRank(b);

    if (aStatusRank !== bStatusRank) return aStatusRank - bStatusRank;

    const aDate = getPoCloseTime(a);
    const bDate = getPoCloseTime(b);

    if (aDate !== bDate) return bDate - aDate;
    return String(a?.name || "").localeCompare(String(b?.name || ""));
  });
};

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [preorderProducts, setPreorderProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const { user } = useAuth();
  const { t } = useLanguage();

  useEffect(() => {
    API.get("/products?stock_type=ready_stock&limit=8").then((r) => setProducts(r.data.products)).catch(() => {});
    API.get("/products?stock_type=preorder&limit=4").then((r) => setPreorderProducts(sortProductsByClosePo(r.data.products || []))).catch(() => {});
    API.get("/brands").then((r) => setBrands(r.data.brands)).catch(() => {});
  }, []);

  const isReseller = ["reseller", "agen", "mitra", "produsen", "distributor", "admin"].includes(user?.role);

  return (
    <div className="min-h-screen" data-testid="home-page">
      <Header />

      {/* Hero */}
      <section className="relative h-[85vh] min-h-[600px] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Haniyah Gallery Family Collection" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-900/60 via-stone-900/30 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-xl">
            <p className="font-sans text-xs uppercase tracking-[0.3em] text-white/70 mb-4 fade-in">{t("home.hero_tag")}</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-6 fade-in stagger-1">
              {t("home.hero_title")}
            </h1>
            <p className="font-sans text-base text-white/80 mb-8 leading-relaxed fade-in stagger-2">
              {t("home.hero_desc")}
            </p>
            <div className="flex flex-wrap gap-4 fade-in stagger-3">
              <Link to="/products" className="bg-primary text-primary-foreground px-8 py-3.5 text-sm uppercase tracking-wider hover:opacity-90 transition-colors inline-flex items-center gap-2" data-testid="hero-shop-btn">
                {t("home.shop_now")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/products?stock_type=preorder" className="border border-white/50 text-white px-8 py-3.5 text-sm uppercase tracking-wider hover:bg-white/10 transition-colors" data-testid="hero-preorder-btn">
                {t("home.preorder")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 sm:py-24" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-sans text-xs uppercase tracking-[0.2em] text-stone-400 font-semibold mb-2">{t("home.browse_by")}</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-stone-900">{t("home.categories_title")}</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.name}
                to={`/products?category=${cat.name}`}
                className="group flex flex-col items-center p-6 bg-white border border-stone-100 hover:border-primary/30 hover:shadow-sm transition-all"
                data-testid={`category-${cat.name.toLowerCase().replace(" ", "-")}`}
              >
                <span className="text-2xl mb-3">{cat.icon}</span>
                <span className="text-sm font-medium text-stone-700 group-hover:text-primary transition-colors">{cat.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PreOrder Highlights */}
      {preorderProducts.length > 0 && (
        <section className="py-16 sm:py-24 bg-[#F5F5F0]" data-testid="preorder-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="font-sans text-xs uppercase tracking-[0.2em] text-amber-600 font-semibold mb-2">{t("home.limited_time")}</p>
                <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-stone-900">{t("home.preorder_open")}</h2>
              </div>
              <Link to="/products?stock_type=preorder" className="text-sm text-primary hover:opacity-80 flex items-center gap-1">
                {t("home.view_all")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
              {preorderProducts.map((p) => (
                <ProductCard key={p.id} product={p} showReseller={isReseller} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Ready Stock */}
      <section className="py-16 sm:py-24" data-testid="ready-stock-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="font-sans text-xs uppercase tracking-[0.2em] text-emerald-600 font-semibold mb-2">{t("home.available_now")}</p>
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-stone-900">{t("home.ready_stock")}</h2>
            </div>
            <Link to="/products?stock_type=ready_stock" className="text-sm text-primary hover:opacity-80 flex items-center gap-1">
              {t("home.view_all")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} showReseller={isReseller} />
            ))}
          </div>
        </div>
      </section>

      {/* Family Collection Banner */}
      <section className="relative py-24 sm:py-32" data-testid="family-section">
        <div className="absolute inset-0">
          <img src={CATEGORY_IMG} alt="Family Collection" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-stone-900/50" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="font-sans text-xs uppercase tracking-[0.3em] text-white/60 mb-4">{t("home.family_tag")}</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white mb-6">{t("home.family_title")}</h2>
          <p className="font-sans text-base text-white/80 max-w-xl mx-auto mb-8">
            {t("home.family_desc")}
          </p>
          <Link to="/products?category=Family Set" className="inline-block bg-white text-stone-900 px-8 py-3.5 text-sm uppercase tracking-wider hover:bg-stone-100 transition-colors">
            {t("home.explore_family")}
          </Link>
        </div>
      </section>

      {/* Brands */}
      {brands.length > 0 && (
        <section className="py-16 sm:py-24" data-testid="brands-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="font-sans text-xs uppercase tracking-[0.2em] text-stone-400 font-semibold mb-2">{t("home.trusted")}</p>
                <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-stone-900">{t("home.our_brands")}</h2>
              </div>
              <Link to="/brands" className="text-sm text-primary hover:opacity-80 flex items-center gap-1">
                {t("home.view_all")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {brands.map((b) => (
                <Link
                  key={b.id}
                  to={`/brands/${b.id}`}
                  className="group p-8 bg-white border border-stone-100 hover:border-primary/30 transition-all text-center"
                  data-testid={`brand-card-${b.id}`}
                >
                  <h3 className="font-serif text-xl text-stone-800 group-hover:text-primary transition-colors mb-2">{b.name}</h3>
                  <p className="text-sm text-stone-500 line-clamp-2">{b.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Why Choose Us */}
      <section className="relative py-16 sm:py-24" data-testid="why-section">
        <div className="absolute inset-0 opacity-10">
          <img src={FABRIC_BG} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-stone-900">{t("home.why_us")}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Heart, title: t("home.features.quality_title"), desc: t("home.features.quality_desc") },
              { icon: Truck, title: t("home.features.shipping_title"), desc: t("home.features.shipping_desc") },
              { icon: Shield, title: t("home.features.brands_title"), desc: t("home.features.brands_desc") },
              { icon: RefreshCw, title: t("home.features.returns_title"), desc: t("home.features.returns_desc") },
            ].map((f) => (
              <div key={f.title} className="text-center p-6">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full mb-4">
                  <f.icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-sans text-sm font-semibold text-stone-800 mb-2">{f.title}</h3>
                <p className="font-sans text-sm text-stone-500">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
